/**
 * Simplified Chatbot Types
 * Focused on request/response interactions with long response times
 */

// Message types
export enum MessageType {
  USER = 'user',
  ASSISTANT = 'assistant',
  SYSTEM = 'system', // For error messages, notifications
  TOOL = 'tool',     // For tool outputs
}

// Message status during sending/receiving
export enum MessageStatus {
  PENDING = 'pending',     // Being sent
  SENT = 'sent',          // Successfully sent
  FAILED = 'failed',      // Failed to send
  RETRYING = 'retrying',  // Being retried
}

// Response processing stages
export enum ResponseStage {
  IDLE = 'idle',           // Default state, no active response
  WAITING = 'waiting',     // Waiting for response to start
  STREAMING = 'streaming', // Actively receiving streamed content
  TOOL_CALLING = 'tool_calling', // Tool is being executed
  ERROR = 'error',         // Error occurred
}

// Chatbot UI states
export enum ChatbotState {
  CLOSED = 'closed',
  OPENING = 'opening',
  OPEN = 'open',
  COLLAPSED = 'collapsed',
  CLOSING = 'closing'
}

// Structured content for approval requests and questionnaires
export interface StructuredContent {
  response?: string;              // Main message text
  message?: string;               // Alternative message field
  text?: string;                  // Alternative text field
  answer?: string;                // Answer field from new API
  requires_approval?: boolean;    // Whether this message needs approval
  approval_data?: ApprovalData;   // Full approval details
  is_task_delegated?: boolean;    // Whether task is already delegated
  request_type?: string;          // Type of request
  request_text?: string;          // Additional request info
  requires_questionnaire?: boolean; // Whether this message has a questionnaire
  questionnaire_data?: QuestionnaireData; // Questionnaire questions
  questionnaire_completed?: boolean; // Whether questionnaire was submitted
  questionnaire_answers?: QuestionnaireAnswer[]; // Submitted answers (for display)
}

// Approval data interfaces
export interface ApprovalData {
  company_name: string;
  conversation_id: string;
  message?: string;
  type: "write_tool_approval";
  pending_tools: PendingTool[];
}

export interface PendingTool {
  tool_name: string;           // e.g., "delegate_task_to_experts"
  tool_call_id: string;        // UUID for tracking
  approval_text: string;       // Human-readable description (can be 500+ chars)
  tool_args?: {                // Optional additional parameters
    client_requirements_note?: string;
    [key: string]: any;
  };
}

// Structured questionnaire types
export interface QuestionnaireQuestion {
  id: string;                  // Unique question identifier (e.g., "q1")
  prompt: string;              // The question text
  options: string[];           // 2-5 preset answer options
  allow_freeform: boolean;     // Whether to show an "Other" text input
  multi_select?: boolean;      // If true, user can select multiple options before submitting
  // Optional nudge tagging — when present, renders NudgeCard above the question
  nudge_id?: string;
  nudge_type?: 'todo' | 'question' | 'update';
  nudge_title?: string;
  nudge_severity?: 'critical' | 'warning' | 'info';
}

export interface QuestionnaireAnswer {
  question_id: string;         // Matches QuestionnaireQuestion.id
  selected_option: string | null;    // Single-select: the preset option chosen
  selected_options?: string[] | null; // Multi-select: all options chosen
  freeform_text: string | null;      // Custom text if "Other" was chosen
}

export interface QuestionnaireData {
  questions: QuestionnaireQuestion[];
  conversation_id: string;
}

// Simple message interface
export interface ChatbotMessage {
  id: string;
  type: MessageType;
  content: string | StructuredContent; // Support both string and structured content
  timestamp: number;
  status: MessageStatus;
  retryCount?: number;
  isDelegationApproved?: boolean; // Track approval state
  toolsUsed?: string[]; // Tools that were used in this message
  toolMarkers?: Array<{ position: number; tool: string; runId?: string; message?: string; success?: boolean | null }>; // Tool markers at specific positions in text (null = in progress, true = success, false = error)
  suggestedResponses?: string[]; // Suggested follow-up responses from the agent
  images?: UploadedImage[]; // Support for uploaded images
  hidden?: boolean; // Whether this message should be hidden from display
  display?: string; // Optional display text shown in UI instead of content (e.g. short label for long messages)
  // Tool fields
  tool_name?: string;
  tool_args?: Record<string, any>;
  tool_output?: string;
  is_error?: boolean;
  tool_call_id?: string;
}

// Message capacity tracking
export interface MessageCapacity {
  tokenCount: number; // Total tokens in conversation
  capacityPercentage: number; // 0-100, based on token count
  maxTokens: number; // Maximum tokens for 100% capacity (200,000)
  isAtLimit: boolean; // true when capacity >= 100% or token count >= max
}

// Continuation state for context overflow recovery
export interface ContinuationState {
  isRecovering: boolean; // True while generating summary (~35 seconds)
  originalConversationId: string | null; // Conversation that overflowed
  retryMessage: string | null; // Message to retry after continuation
  recoveryStartTime: number | null; // When recovery started (for timeout tracking)
}

export type StreamInterruptionSource = 'network' | 'llm' | 'unknown';
export type StreamInterruptionPhase = 'idle' | 'silent' | 'toast' | 'resume' | 'retry' | 'blocked';
export type InterruptAction = 'resume' | 'retry' | 'none';

// Stream interrupted state for retry/resume handling
export interface StreamInterruptedState {
  isInterrupted: boolean; // True when stream was interrupted mid-response
  partialResponse: string; // Partial response user already saw
  error: string; // Error message from backend
  isRetryable: boolean; // Whether the error is retryable
  originalMessage: string; // Original user message to retry
  uploadedImages: UploadedImage[]; // Original uploaded images to preserve on retry/resume
  interruptedAt: number | null; // Timestamp when interruption occurred
  conversationId: string | null; // Conversation ID for resume
  source: StreamInterruptionSource; // Network vs LLM interruption
  phase: StreamInterruptionPhase; // Single source of truth for UI phase
  disconnectStartedAt: number | null; // When network disconnect started
  lastReconnectAttemptAt: number | null; // Last reconnect attempt time
}

// SSE connection state for tracking disconnections
export interface SSEConnectionState {
  isConnected: boolean; // Current connection state
  disconnectedAt: number | null; // When connection was lost
  lastEventId: number; // Last event ID received (for resume)
  reconnectAttempts: number; // Number of reconnection attempts
}

// Uploaded image interface
export interface UploadedImage {
  file: File | null; // File object (null for loaded images)
  name?: string; // Asset name
  url?: string; // Presigned URL for display
  uploaded?: boolean; // Whether upload is complete
  s3_url?: string; // S3 URL after upload
  asset_id?: string; // Asset ID from backend
  s3_path_to_thumbnail?: string; // Thumbnail presigned URL
  description?: string; // Image description from Groq annotation
}

export type ExecutionTodoItemStatus = 'pending' | 'in_progress' | 'completed' | 'cancelled';

export interface ExecutionTodoItem {
  id: string;
  title: string;
  description?: string;
  status: ExecutionTodoItemStatus;
  order: number;
  note?: string;
  updated_at?: string;
}

export interface ExecutionTodo {
  title: string;
  version: number;
  status: 'active' | 'completed' | 'cancelled';
  source: string;
  created_at?: string;
  updated_at: string;
  active_item_id?: string | null;
  items: ExecutionTodoItem[];
  last_update?: {
    type?: string;
    note?: string;
    updated_at?: string;
    changes?: string[];
  };
}

// Response progress tracking
export interface ResponseProgress {
  requestId: string;
  stage: ResponseStage;
  progress: number; // 0-100
  startTime: number;
  estimatedDuration: number; // in ms
  currentActivity: string;
  canCancel: boolean;
  streamedContent?: string; // For streaming updates
  activeTools?: string[]; // Tools currently active
  toolMarkers?: Array<{ position: number; tool: string; runId?: string; message?: string; success?: boolean | null }>; // Tool markers at specific positions in text (null = in progress, true = success, false = error)
  messageCapacity?: MessageCapacity; // Message capacity info
  isStreamReset?: boolean; // When true, the store must clear ALL local streaming state (worker death recovery)
  executionTodo?: ExecutionTodo | null; // Live execution todo snapshot
}

// Error information
export interface ChatbotError {
  id: string;
  code: string;
  message: string;
  timestamp: number;
  retryable: boolean;
  retryAfter?: number; // ms
  originalRequest?: string;
}

// Chatbot settings
export interface ChatbotSettings {
  mockMode: boolean;
}

// Chat history interfaces
export interface ConversationHistoryItem {
  conversation_id: string;
  title: string;
  timestamp: number;
  last_activity_at?: number; // Epoch seconds of last message activity (for "last used" sorting)
  preview?: string; // First message preview
  started_by_user_name?: string; // Name of the user who started this conversation
}

// UI view states
export enum ChatbotView {
  CHAT = 'chat',
  HISTORY = 'history',
}

// Main chatbot state
export interface ChatbotStoreState {
  // Core conversation data
  messages: ChatbotMessage[];
  conversationId: string | undefined;

  // UI state
  state: ChatbotState;
  responseStage: ResponseStage;
  currentView: ChatbotView;
  inputValue: string;
  inputFocusRequestId: number;
  inputFocused: boolean;
  inputPlaceholderOverride: string | null;
  showWelcomeScreen: boolean;

  // Pending artifacts queued from outside the ChatbotContext (e.g. Report iterate)
  // Consumed by ChatbotContext and moved into chatArtifacts on render
  pendingArtifacts: import('@customTypes/artifact').Artifact[];

  // Response tracking
  currentResponse: ResponseProgress | null;
  activeTools: string[]; // Tools currently being used

  // Message capacity tracking
  messageCapacity: MessageCapacity | null;
  executionTodo: ExecutionTodo | null;

  // Continuation state for context overflow recovery
  continuation: ContinuationState;

  // Stream interrupted state for retry/resume
  streamInterrupted: StreamInterruptedState;

  // SSE connection state
  sseConnection: SSEConnectionState;

  // Chat history
  conversationHistory: ConversationHistoryItem[];
  historyLoading: boolean;
  historySearchText: string;
  conversationLoading: boolean; // Loading state for loading a conversation by ID

  // Error handling
  error: ChatbotError | null;

  // Settings
  settings: ChatbotSettings;
  
  // Side modal state
  isAssetHubModalOpen: boolean;
}

export interface OpenChatbotOptions {
  initialMessage?: string;
  hideInitialMessage?: boolean;
  displayText?: string;
  prefillInput?: string;
  prefillArtifacts?: import('@customTypes/artifact').Artifact[];
}

// Store actions
export interface ChatbotActions {
  // Lifecycle
  openChatbot: (options?: OpenChatbotOptions) => void;
  collapseChat: () => void;

  // Messaging
  sendMessage: (content: string, images?: UploadedImage[], hidden?: boolean, display?: string) => Promise<void>;
  retryMessage: (messageId: string) => Promise<void>;
  retryFromError: () => Promise<void>;
  cancelCurrentResponse: () => void | Promise<void>;
  clearConversation: () => void;

  // Input handling
  setInputValue: (value: string) => void;
  setInputFocused: (value: boolean) => void;
  setInputPlaceholderOverride: (value: string | null) => void;
  requestInputFocus: () => void;
  clearPendingArtifacts: () => void;

  // Progress tracking
  updateResponseProgress: (progress: Partial<ResponseProgress>) => void;
  setResponseStage: (stage: ResponseStage, activity?: string) => void;
  setActiveTools: (tools: string[]) => void;
  setExecutionTodo: (todo: ExecutionTodo | null) => void;

  // Error handling
  setError: (error: ChatbotError | null) => void;
  clearError: () => void;

  // Settings
  updateSettings: (settings: Partial<ChatbotSettings>) => void;
  toggleMockMode: () => void;

  // Conversation management
  setConversationId: (id: string | undefined) => void;
  loadConversationFromStorage: () => void;
  initializeStore: () => Promise<void>;

  // Chat history management
  setCurrentView: (view: ChatbotView) => void;
  loadConversationHistory: () => Promise<void>;
  setHistorySearchText: (text: string) => void;
  loadConversationById: (conversationId: string) => Promise<void>;
  saveCurrentConversation: () => Promise<void>;
  deleteConversation: (conversationId: string) => Promise<void>;

  // Approval actions
  approveTask: (messageId: string) => Promise<void>;
  cancelApproval: (messageId: string) => void;

  // Questionnaire actions
  submitQuestionnaireAnswers: (messageId: string, answers: import('./types').QuestionnaireAnswer[]) => Promise<void>;
  
  // Side modal actions
  openAssetHubModal: () => void;
  closeAssetHubModal: () => void;

  // Continuation actions for context overflow recovery
  handleOverflowRecoveryStarted: (originalConversationId: string) => void;
  handleContinuationComplete: (newConversationId: string, retryMessage: string, continuedFrom: string, continuationChain: string[]) => Promise<void>;
  handleOverflowError: (errorMessage: string) => void;
  clearContinuationState: () => void;

  // Stream interrupted actions for retry/resume
  handleStreamInterrupted: (
    partialResponse: string,
    error: string,
    isRetryable: boolean,
    originalMessage: string,
    uploadedImages?: UploadedImage[]
  ) => void;
  retryInterruptedMessage: () => Promise<void>;
  resumeStream: () => Promise<void>;
  clearStreamInterruptedState: () => void;

  // Stream lifecycle
  detachFromStream: () => void;

  // SSE connection tracking
  updateSSEConnectionState: (state: Partial<SSEConnectionState>) => void;
  updateStreamInterruptedState: (state: Partial<StreamInterruptedState>) => void;
  handleSSEDisconnect: () => void;
  handleSSEReconnect: () => void;
  
  // Internal actions (for store use)
  addMessage: (message: Omit<ChatbotMessage, 'id' | 'timestamp'>) => void;
  updateMessage: (id: string, updates: Partial<ChatbotMessage>) => void;
  setState: (state: ChatbotState) => void;

  // Private helper methods
  _handleMockResponse: (content: string, requestId: string) => Promise<void>;
  _handleRealResponse: (content: string, requestId: string, conversationId?: string, uploadedImages?: any[], isRetry?: boolean, isHidden?: boolean, displayText?: string) => Promise<void>;
}

// Combined store type
export type ChatbotStore = ChatbotStoreState & ChatbotActions;

// API request/response types
export interface ChatbotRequest {
  message: string;
  requestId: string;
  timestamp: number;
  conversationId?: string;
  conversationContext?: string[];
}

export interface ChatbotResponse {
  message: string;
  requestId: string;
  timestamp: number;
  processingTime: number;
  conversationId?: string;
  error?: {
    code: string;
    message: string;
  };
}

// Progress update from server (future enhancement)
export interface ServerProgressUpdate {
  requestId: string;
  stage: ResponseStage;
  progress: number;
  estimatedTimeRemaining: number;
  currentActivity: string;
}

// Mock response configuration
export interface MockResponseConfig {
  delay: number; // base delay in ms
  variability: number; // random variation (0-1)
  stages: Array<{
    stage: ResponseStage;
    duration: number; // % of total time
    activity: string;
  }>;
}